import { useState } from "react";
import { Check, X, Crown, Sparkles, Medal } from "lucide-react";
import { Reveal, SectionHeader } from "./Shared";
import { planWhatsAppUrl } from "@/lib/site";

const CYCLES = [
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "3-Month", note: "Save ~20%" },
  { key: "yearly", label: "1-Year", note: "Best Value" },
];

const PLANS = [
  {
    tier: "ESSENTIAL",
    icon: Medal,
    badge: "For Emerging Traders",
    minCapital: "RM 10,000",
    target: "RM 300 — RM 500",
    features: { syariah: true, hsi: false, ipo: false, handling: false },
    price: { monthly: 499, quarterly: 1199, yearly: 2999 },
  },
  {
    tier: "PREMIUM",
    icon: Sparkles,
    badge: "Most Popular Choice",
    minCapital: "RM 30,000",
    target: "RM 1,500 — RM 2,000",
    features: { syariah: true, hsi: true, ipo: false, handling: false },
    price: { monthly: 1499, quarterly: 2999, yearly: 5999 },
    featured: true,
  },
  {
    tier: "VIP",
    icon: Crown,
    badge: "Institutional & Wealth",
    minCapital: "RM 100,000+",
    target: "RM 5,000 — RM 10,000+",
    features: { syariah: true, hsi: true, ipo: true, handling: true },
    price: { monthly: 2999, quarterly: 5999, yearly: 9999 },
    gold: true,
  },
];

const FEATURE_ROWS = [
  { key: "syariah", label: "Syariah Signals" },
  { key: "hsi", label: "HSI Signals" },
  { key: "ipo", label: "IPO Trading" },
  { key: "handling", label: "Account Handling" },
];

const CYCLE_LABEL = { monthly: "Monthly", quarterly: "3-Month", yearly: "1-Year" };

export default function Pricing() {
  const [cycle, setCycle] = useState("monthly");

  return (
    <section id="pricing" data-testid="pricing-section" className="border-t border-line bg-surface/40 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          align="center"
          eyebrow="Membership"
          title="Choose the plan that matches your ambition."
          sub="All tiers cover Bursa Malaysia, Forex & Comex, and SGX & Nasdaq desks. Fees in USD, billed per cycle."
        />

        <Reveal delay={0.1} className="mt-10 flex justify-center">
          <div data-testid="billing-toggle" className="inline-flex flex-wrap justify-center gap-1 rounded-full border border-line bg-panel p-1.5">
            {CYCLES.map((c) => (
              <button
                key={c.key}
                data-testid={`billing-toggle-${c.key}`}
                onClick={() => setCycle(c.key)}
                className={`rounded-full px-5 py-2.5 text-xs font-bold transition-colors duration-300 ${
                  cycle === c.key ? "bg-gold text-obsidian" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {c.label}
                {c.note && <span className={`ml-2 font-mono text-[9px] ${cycle === c.key ? "text-obsidian/70" : "text-alpha"}`}>{c.note}</span>}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-3 lg:gap-8">
          {PLANS.map((p, idx) => (
            <Reveal key={p.tier} delay={idx * 0.12}>
              <article
                data-testid={`plan-card-${p.tier.toLowerCase()}`}
                className={`relative flex h-full flex-col rounded-2xl border bg-panel p-7 transition-transform duration-500 hover:-translate-y-2 ${
                  p.featured
                    ? "glow-alpha border-alpha/60 lg:-mt-4 lg:mb-[-1rem]"
                    : p.gold
                      ? "glow-gold border-gold/50"
                      : "border-line"
                }`}
              >
                <span
                  className={`absolute -top-3 left-6 rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] ${
                    p.featured ? "bg-alpha text-obsidian" : p.gold ? "bg-gold text-obsidian" : "bg-line text-slate-300"
                  }`}
                >
                  {p.badge}
                </span>

                <div className="flex items-center gap-3 pt-2">
                  <p.icon size={22} className={p.gold ? "text-gold" : "text-alpha"} />
                  <h3 className="font-display text-2xl font-extrabold tracking-tight text-slate-100">{p.tier}</h3>
                </div>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-mono text-4xl font-bold text-slate-100" data-testid={`plan-price-${p.tier.toLowerCase()}`}>
                    ${p.price[cycle].toLocaleString()}
                  </span>
                  <span className="font-mono text-xs text-slate-500">USD / {CYCLE_LABEL[cycle]}</span>
                </div>

                <div className="mt-6 space-y-3 border-t border-line pt-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Min. Capital</span>
                    <span className="font-mono font-semibold text-slate-200">{p.minCapital}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Target / Trade</span>
                    <span className="font-mono font-semibold text-alpha">{p.target}</span>
                  </div>
                </div>

                <ul className="mt-4 flex-1 space-y-3">
                  {FEATURE_ROWS.map((f) => (
                    <li key={f.key} className="flex items-center gap-3 text-sm">
                      {p.features[f.key] ? (
                        <Check size={16} className="shrink-0 text-alpha" />
                      ) : (
                        <X size={16} className="shrink-0 text-slate-600" />
                      )}
                      <span className={p.features[f.key] ? "text-slate-300" : "text-slate-600 line-through"}>{f.label}</span>
                    </li>
                  ))}
                </ul>

                <a
                  data-testid={`plan-cta-${p.tier.toLowerCase()}`}
                  href={planWhatsAppUrl(p.tier, CYCLE_LABEL[cycle])}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-8 block rounded-full py-3.5 text-center text-sm font-bold transition-transform duration-300 hover:scale-[1.03] ${
                    p.featured
                      ? "bg-alpha text-obsidian"
                      : p.gold
                        ? "bg-gold text-obsidian"
                        : "border border-slate-500 text-slate-200 hover:border-slate-300"
                  }`}
                >
                  Join {p.tier} via WhatsApp
                </a>
              </article>
            </Reveal>
          ))}
        </div>

        <p className="mt-10 text-center font-mono text-[11px] text-slate-600">
          All plans include real-time WhatsApp &amp; Telegram delivery, exact entry / stop-loss / target levels, and MYT desk support.
        </p>
      </div>
    </section>
  );
}
